import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import TypeAheadLookup from '../../components/TypeAheadLookup';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { Send, X, CheckCircle, Clock, Mail, Users } from 'lucide-react';

export function SuppliersTab({ invited, setInvited, interested, eventRef, form }) {
  const [resolvedNames, setResolvedNames] = useState({});
  const [invitationStatus, setInvitationStatus] = useState({});
  const [sending, setSending] = useState({});

  // Resolve supplier codes to names
  useEffect(() => {
    const codes = (invited || []).filter(s => typeof s === 'string').filter(c => !resolvedNames[c]);
    if (codes.length === 0) return;
    const resolve = async () => {
      const names = { ...resolvedNames };
      for (const code of codes) {
        try {
          const r = await api.get('/api/data/suppliers', { params: { search: code, page_size: 1 } });
          const match = (r.data.data || r.data || []).find(s => s.supplier_code === code);
          names[code] = match ? (match.supplier_name || match.name || code) : code;
        } catch { names[code] = code; }
      }
      setResolvedNames(names);
    };
    resolve();
  }, [invited]); // eslint-disable-line

  // Load invitation status
  useEffect(() => {
    if (!eventRef) return;
    api.get(`/api/rfx/workflow/invitation-status/${eventRef}`)
      .then(r => {
        const map = {};
        for (const rec of (r.data.data || [])) {
          map[rec.supplier_code] = rec;
        }
        setInvitationStatus(map);
      })
      .catch(() => {});
  }, [eventRef]);

  const addSupplier = (code, name) => {
    if (!code) return;
    const existing = invited || [];
    if (existing.some(s => (typeof s === 'string' ? s : s.supplier_code) === code)) return;
    setInvited([...existing, { supplier_code: code, supplier_name: name }]);
  };

  const removeSupplier = (code) => {
    setInvited((invited || []).filter(s => (typeof s === 'string' ? s : s.supplier_code) !== code));
  };

  const getCode = (s) => typeof s === 'string' ? s : s.supplier_code;
  const getName = (s) => {
    if (typeof s === 'string') return resolvedNames[s] || s;
    return s.supplier_name || s.supplier_code;
  };

  // Check mandatory fields before allowing invitations
  const getMissingFields = () => {
    const missing = [];
    if (!form?.title?.trim()) missing.push('Title');
    if (!form?.description?.trim()) missing.push('Description');
    if (!form?.start_date) missing.push('Start Date');
    if (!form?.end_date) missing.push('End Date');
    if (!form?.closing_date) missing.push('Closing Date');
    return missing;
  };

  const canInvite = getMissingFields().length === 0 && !!eventRef;

  const sendInvitation = async (code) => {
    const missing = getMissingFields();
    if (missing.length > 0) { toast.error(`Complete required fields before inviting: ${missing.join(', ')}`); return; }
    if (!eventRef) { toast.error('Save the event first'); return; }
    setSending(prev => ({ ...prev, [code]: true }));
    try {
      const r = await api.post(`/api/rfx/workflow/invite/${eventRef}/${code}`);
      toast.success(r.data.message);
      setInvitationStatus(prev => ({
        ...prev,
        [code]: { ...prev[code], invitation_sent: true, invitation_sent_at: r.data.sent_at, state: 'INVITED' },
      }));
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to send invitation')); }
    setSending(prev => ({ ...prev, [code]: false }));
  };

  const sendAllInvitations = async () => {
    const missing = getMissingFields();
    if (missing.length > 0) { toast.error(`Complete required fields before inviting: ${missing.join(', ')}`); return; }
    if (!eventRef) { toast.error('Save the event first'); return; }
    setSending(prev => ({ ...prev, _all: true }));
    try {
      const r = await api.post(`/api/rfx/workflow/invite-all/${eventRef}`);
      toast.success(r.data.message);
      // Reload status
      const sr = await api.get(`/api/rfx/workflow/invitation-status/${eventRef}`);
      const map = {};
      for (const rec of (sr.data.data || [])) map[rec.supplier_code] = rec;
      setInvitationStatus(map);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
    setSending(prev => ({ ...prev, _all: false }));
  };

  const allInvited = (invited || []).every(s => invitationStatus[getCode(s)]?.invitation_sent);
  const uninvitedCount = (invited || []).filter(s => !invitationStatus[getCode(s)]?.invitation_sent).length;

  return (
    <div className="space-y-4 py-2">
      {/* Search & Add */}
      <div>
        <Label className="font-semibold text-sm">Search Suppliers to Invite</Label>
        <div className="mt-1">
          <TypeAheadLookup
            endpoint="/api/data/suppliers"
            searchParam="search"
            valueField="supplier_code"
            labelField="supplier_name"
            secondaryField="supplier_code"
            placeholder="Type supplier name or code..."
            value=""
            displayValue=""
            clearOnSelect
            onChange={(code, record) => {
              if (code && record) addSupplier(code, record.supplier_name || record.name || code);
            }}
            excludeValues={(invited || []).map(s => getCode(s))}
            data-testid="supplier-search"
          />
        </div>
      </div>

      {/* Warning if mandatory fields missing */}
      {getMissingFields().length > 0 && (invited || []).length > 0 && (
        <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 text-xs text-amber-800">
          <strong>Invitations disabled</strong> — complete required Details fields before sending: {getMissingFields().join(', ')}
        </div>
      )}

      {/* Invited Suppliers with invitation status */}
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Users className="w-4 h-4 text-slate-500" /> Invited Suppliers ({(invited || []).length})
          </h3>
          {eventRef && uninvitedCount > 0 && (
            <Button size="sm" onClick={sendAllInvitations} disabled={sending._all || !canInvite}
              className={canInvite ? "bg-indigo-600 hover:bg-indigo-700" : "bg-slate-300 cursor-not-allowed"} data-testid="send-all-invitations-btn">
              <Mail className="w-3.5 h-3.5 mr-1" /> {sending._all ? 'Sending...' : `Invite All (${uninvitedCount})`}
            </Button>
          )}
          {eventRef && allInvited && (invited || []).length > 0 && (
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><CheckCircle className="w-3 h-3 mr-0.5 inline" /> All Invited</Badge>
          )}
        </div>

        {(invited || []).length === 0 ? (
          <p className="text-xs text-slate-400">No suppliers added yet. Use the search above to find and add suppliers.</p>
        ) : (
          <div className="space-y-1.5">
            {(invited || []).map(s => {
              const code = getCode(s);
              const name = getName(s);
              const status = invitationStatus[code];
              const isSent = status?.invitation_sent;
              const state = status?.state;

              return (
                <div key={code} className="flex items-center gap-2 py-1.5 px-3 rounded-lg border bg-white hover:bg-slate-50 transition-colors" data-testid={`supplier-row-${code}`}>
                  {/* Invitation status indicator */}
                  {isSent ? (
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" title="Invitation sent" />
                  ) : (
                    <Clock className="w-4 h-4 text-slate-300 flex-shrink-0" title="Not yet invited" />
                  )}

                  {/* Name */}
                  <span className="text-sm font-medium flex-1">{name}</span>
                  <span className="text-xs text-slate-400">{code}</span>

                  {/* State badge */}
                  {state && (
                    <Badge className={`text-[9px] ${
                      state === 'ACCEPTED' ? 'bg-emerald-100 text-emerald-700' :
                      state === 'DECLINED' ? 'bg-red-100 text-red-700' :
                      state === 'SUBMITTED' ? 'bg-blue-100 text-blue-700' :
                      state === 'SHORTLISTED' ? 'bg-violet-100 text-violet-700' :
                      state === 'AWARDED' ? 'bg-emerald-100 text-emerald-800' :
                      state === 'INVITED' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>{state}</Badge>
                  )}

                  {/* Sent timestamp */}
                  {isSent && status.invitation_sent_at && (
                    <span className="text-[10px] text-slate-400" title={`Invited ${new Date(status.invitation_sent_at).toLocaleString()}`}>
                      {new Date(status.invitation_sent_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'America/Los_Angeles' })}
                    </span>
                  )}

                  {/* Actions */}
                  {eventRef && !isSent && (
                    <Button size="sm" variant="outline" className={`h-6 text-xs px-2 ${!canInvite ? 'opacity-50' : ''}`} onClick={() => sendInvitation(code)}
                      disabled={sending[code] || !canInvite} data-testid={`invite-btn-${code}`}>
                      <Send className="w-3 h-3 mr-1" /> {sending[code] ? '...' : 'Invite'}
                    </Button>
                  )}
                  {eventRef && isSent && (
                    <button onClick={() => sendInvitation(code)} disabled={sending[code] || !canInvite}
                      className={`p-1 ${canInvite ? 'text-amber-500 hover:text-amber-700' : 'text-slate-300'}`} title={canInvite ? 'Resend invitation' : 'Complete Details first'}>
                      <Send className="w-3 h-3" />
                    </button>
                  )}
                  <button onClick={() => removeSupplier(code)} className="text-slate-300 hover:text-red-500 p-1" title="Remove">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Interested Suppliers (from public portal) */}
      {(interested || []).length > 0 && (
        <div className="border rounded-lg p-4">
          <h3 className="text-sm font-semibold mb-2">Suppliers Who Expressed Interest ({interested.length})</h3>
          <div className="space-y-1">
            {interested.map((s, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-3 text-xs border-b last:border-0">
                <span className="font-medium">{s.supplier_name || s.supplier_email}</span>
                <div className="flex items-center gap-2">
                  <Badge className="text-[9px]" variant="outline">{s.status}</Badge>
                  <span className="text-slate-400">{s.expressed_at}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
